/* eslint-disable max-lines */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { CopyIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import {
  type AwsRole,
  awsRoleQueries,
  deleteAwsRole,
  upsertAwsRole,
} from '@/lib/queries/integrations/cloud/aws/roles';
import type { Project } from '@/lib/queries/project';
import { generateMutationKey } from '@/lib/queries/utils';
import { removeEmptyStrings } from '@/lib/utils';

import awsLogo from '../../../-components/logos/aws.svg?url';
import { trustRelationShip } from './trustRelationShip';
import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  initialValue?: AwsRole;
  mainProject: Project;
}

export const AwsRoleForm = ({
  initialValue,
  mainProject,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const { toast } = useToast();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      ...initialValue,
    },
  });

  const { isPending, mutateAsync } = useMutation({
    mutationKey: generateMutationKey(
      awsRoleQueries.byProject(mainProject.id),
      'upsert',
      true,
    ),
    mutationFn: async (values: FormValues) => {
      await upsertAwsRole(
        mainProject.id,
        removeEmptyStrings(values),
        initialValue?.id,
      );
    },
    onSuccess: async () => {
      router.navigate({ to: '/integrations/aws' });
    },
    onError: () => {
      toast({
        title:
          initialValue !== undefined
            ? 'configuration.awsRole.form.update.error'
            : 'configuration.awsRole.form.create.error',
        variant: 'destructive',
      });
    },
  });

  const { isPending: isDeleting, mutateAsync: deleteMutation } = useMutation({
    mutationKey: generateMutationKey(
      awsRoleQueries.list(mainProject.id),
      'delete',
      true,
    ),
    mutationFn: async (roleId: string) => {
      await deleteAwsRole(mainProject.id, roleId);
    },
    onSuccess: async () => {
      toast({
        title: 'configuration.awsRole.form.delete.success',
        variant: 'default',
      });
      router.navigate({ to: '/integrations/aws' });
    },
    onError: () => {
      toast({
        title: 'configuration.awsRole.form.delete.error',
        variant: 'destructive',
      });
    },
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(async values => mutateAsync(values))}>
        <h1 className="text-3xl mb-6 flex items-center gap-3">
          <img
            src={awsLogo}
            alt="AWS"
            className="h-8 w-8 dark:invert dark:brightness-0 dark:contrast-200"
          />
          <FormattedMessage id="configuration.awsRole.form.title" />
        </h1>
        <div className="flex flex-row gap-4">
          <div className="basis-0 grow">
            <div className="flex flex-col gap-4 mb-5 max-w-md">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage id="configuration.awsRole.form.name.label" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        type="text"
                        placeholder={formatMessage({
                          id: 'configuration.awsRole.form.name.placeholder',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arn"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage id="configuration.awsRole.form.arn.label" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        type="text"
                        placeholder={formatMessage({
                          id: 'configuration.awsRole.form.arn.placeholder',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="externalId"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      <FormattedMessage id="configuration.awsRole.form.externalId.label" />
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        className="bg-gray-50 border border-gray-300 text-gray-900 rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                        placeholder={formatMessage({
                          id: 'configuration.awsRole.form.externalId.placeholder',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex flex-row gap-4 justify-between">
                {initialValue !== undefined ? (
                  <>
                    <Button variant="main" disabled={isPending} type="submit">
                      {isPending ? (
                        <FormattedMessage id="configuration.awsRole.form.update.pending" />
                      ) : (
                        <FormattedMessage id="configuration.awsRole.form.update.label" />
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      className="bg-red-500 hover:bg-red-600"
                      disabled={isDeleting}
                      onClick={() => {
                        deleteMutation(initialValue.id);
                      }}
                      type="button"
                    >
                      {isDeleting ? (
                        <FormattedMessage id="configuration.awsRole.form.delete.pending" />
                      ) : (
                        <FormattedMessage id="configuration.awsRole.form.delete.label" />
                      )}
                    </Button>
                  </>
                ) : (
                  <Button variant="main" disabled={isPending} type="submit">
                    {isPending ? (
                      <FormattedMessage id="configuration.awsRole.form.create.pending" />
                    ) : (
                      <FormattedMessage id="configuration.awsRole.form.create.label" />
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="grow basis-0 h-full">
            <h3 className="text-lg mb-2">
              <FormattedMessage id="configuration.awsRole.form.trustRelationShip" />
            </h3>
            <div className="bg-background/95 p-4 rounded-md relative h-full border border-border">
              <div className="absolute top-0 right-0">
                <Button
                  variant="ghost"
                  className="px-2 m-2"
                  onClick={() => {
                    navigator.clipboard.writeText(trustRelationShip);
                    toast({ title: 'global.copiedToClipboard' });
                  }}
                  type="button"
                >
                  <CopyIcon className="cursor-pointer" />
                </Button>
              </div>
              <div className="overflow-y-auto h-full text-xs">
                <pre>
                  <code lang="json">{trustRelationShip}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
};
